import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subscription } from 'rxjs';
import { TourService } from '../../../core/services/tour.service';
import { TourStep, TourConfig } from '../../../core/models/tour-step.model';

@Component({
    selector: 'app-tour',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatProgressBarModule
    ],
    template: `
        <div class="tour-overlay" *ngIf="isActive" (click)="onOverlayClick($event)">
            <div class="tour-highlight" [style]="highlightStyle" [class.tour-highlight--visible]="isPositioned"></div>
            <div class="tour-tooltip" [style]="tooltipStyle" [class.tour-tooltip--visible]="isPositioned" *ngIf="currentStep">
                <mat-card class="tour-card">
                    <mat-card-header>
                        <div class="tour-header">
                            <div class="tour-title-section">
                                <h3 class="tour-title">{{ currentStep.title }}</h3>
                                <span class="tour-progress" *ngIf="showProgress">
                                    Step {{ currentStepIndex + 1 }} of {{ totalSteps }}
                                </span>
                            </div>
                            <button mat-icon-button class="tour-close" (click)="skipTour()" aria-label="Close tour">
                                <mat-icon>close</mat-icon>
                            </button>
                        </div>
                    </mat-card-header>
                    <mat-card-content>
                        <p class="tour-text">{{ currentStep.text }}</p>
                        <mat-progress-bar 
                            *ngIf="showProgress" 
                            mode="determinate" 
                            [value]="progressValue"
                            class="tour-progress-bar">
                        </mat-progress-bar>
                    </mat-card-content>
                    <mat-card-actions class="tour-actions">
                        <button 
                            mat-stroked-button 
                            (click)="skipTour()"
                            class="tour-skip-btn">
                            Skip Tour
                        </button>
                        <div class="tour-nav-buttons">
                            <button 
                                mat-icon-button 
                                [disabled]="currentStepIndex === 0"
                                (click)="previousStep()"
                                aria-label="Previous step">
                                <mat-icon>arrow_back</mat-icon>
                            </button>
                            <button 
                                mat-raised-button 
                                color="primary"
                                (click)="nextStep()"
                                class="tour-next-btn">
                                {{ isLastStep ? 'Finish' : 'Next' }}
                                <mat-icon>{{ isLastStep ? 'check' : 'arrow_forward' }}</mat-icon>
                            </button>
                        </div>
                    </mat-card-actions>
                </mat-card>
            </div>
        </div>
    `,
    styles: [`
        .tour-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 10000;
            pointer-events: auto;
        }
        
        .tour-highlight {
            position: absolute;
            border: 4px solid var(--primary-color);
            border-radius: 12px;
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55),
                        0 0 0 4px rgba(30, 58, 138, 0.3),
                        0 0 24px rgba(30, 58, 138, 0.6);
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.2s ease;
            z-index: 10001;
            animation: tour-highlight-pulse 2s ease-in-out infinite;
        }
        .tour-highlight--visible {
            opacity: 1;
        }
        
        @keyframes tour-highlight-pulse {
            0%, 100% { box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 0 4px rgba(30, 58, 138, 0.3), 0 0 24px rgba(30, 58, 138, 0.6); }
            50% { box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 0 4px rgba(30, 58, 138, 0.6), 0 0 32px rgba(30, 58, 138, 0.9); }
        }
        
        .tour-tooltip {
            position: absolute;
            z-index: 10002;
            pointer-events: auto;
            max-width: 400px;
            min-width: 320px;
            opacity: 0;
            transition: opacity 0.2s ease;
        }
        .tour-tooltip--visible {
            opacity: 1;
        }
        
        .tour-card {
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
            border-radius: 12px;
            overflow: visible;
        }
        
        .tour-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            width: 100%;
        }
        
        .tour-title-section {
            flex: 1;
        }
        
        .tour-title {
            margin: 0 0 4px 0;
            font-size: 20px;
            font-weight: 600;
            color: var(--text-primary);
        }
        
        .tour-progress {
            font-size: 12px;
            color: var(--text-secondary);
            font-weight: 500;
        }
        
        .tour-close {
            color: var(--text-secondary);
        }
        
        .tour-text {
            margin: 0 0 16px 0;
            color: var(--text-secondary);
            line-height: 1.6;
            font-size: 14px;
        }
        
        .tour-progress-bar {
            margin-top: 8px;
            height: 4px;
        }
        
        .tour-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            margin: 0;
        }
        
        .tour-nav-buttons {
            display: flex;
            gap: 8px;
            align-items: center;
        }
        
        .tour-next-btn {
            display: flex;
            align-items: center;
            gap: 4px;
        }
        
        .tour-skip-btn {
            color: var(--text-secondary);
        }
        
        @media (max-width: 768px) {
            .tour-tooltip {
                max-width: calc(100vw - 32px);
                min-width: calc(100vw - 32px);
                left: 16px !important;
                right: 16px !important;
            }
        }
    `]
})
export class TourComponent implements OnInit, OnDestroy, AfterViewInit {
    isActive = false;
    currentStep: TourStep | null = null;
    currentStepIndex = -1;
    totalSteps = 0;
    highlightStyle = '';
    tooltipStyle = '';
    showProgress = true;
    isPositioned = false;
    private currentTour: TourConfig | null = null;
    
    private subscriptions: Subscription[] = [];
    
    constructor(private tourService: TourService) {}
    
    ngOnInit(): void {
        this.subscriptions.push(
            this.tourService.isActive.subscribe(active => {
                this.isActive = active;
                if (active) {
                    this.updateTourDisplay();
                } else {
                    this.isPositioned = false;
                }
            })
        );
        
        this.subscriptions.push(
            this.tourService.currentStepIndex.subscribe(index => {
                this.currentStepIndex = index;
                this.isPositioned = false;
                this.updateTourDisplay();
            })
        );
        
        this.subscriptions.push(
            this.tourService.currentTour.subscribe(tour => {
                this.currentTour = tour;
                if (tour) {
                    this.totalSteps = tour.steps.length;
                }
            })
        );
    }
    
    ngAfterViewInit(): void {
        // Update display after view init
        setTimeout(() => this.updateTourDisplay(), 100);
    }
    
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
    
    private updateTourDisplay(): void {
        if (!this.isActive) {
            return;
        }
        
        this.currentStep = this.tourService.getCurrentStep();
        
        if (!this.currentStep) {
            return;
        }
        
        this.showProgress = this.currentStep.showProgress !== false;
        
        // Wait for DOM to update after navigation, retrying until element is found
        this.waitForPositionAndShow(this.currentStep!);
    }
    
    /**
     * Polls until the target element appears in the DOM, then snaps the
     * highlight/tooltip into position and fades them in.  This prevents the
     * glitch where the overlay briefly renders at the fallback-center position
     * before the real element is available.
     */
    private waitForPositionAndShow(step: TourStep, elapsed = 0): void {
        const maxWait = step.waitTimeout ?? 5000;
        const interval = 100;

        const position = this.tourService.getElementPosition(step.selector);
        if (position) {
            this.updateHighlight(position);
            this.updateTooltip(position);
            this.isPositioned = true;
            const element = document.querySelector(step.selector.split(',')[0].trim());
            if (element) {
                (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (elapsed < maxWait) {
            setTimeout(() => {
                // Bail out if the step has changed while we were waiting
                if (this.currentStep?.id !== step.id) return;
                this.waitForPositionAndShow(step, elapsed + interval);
            }, interval);
        } else {
            // Element never appeared — fall back to center so the card is still visible
            const rect = this.getFallbackHighlightRect();
            this.updateHighlight(rect);
            this.updateTooltip(rect);
            this.isPositioned = true;
        }
    }

    /** When the target element is not found, show a visible highlight (e.g. main content area or center). */
    private getFallbackHighlightRect(): DOMRect {
        const padding = 40;
        const w = Math.min(400, window.innerWidth - 80);
        const h = Math.min(200, window.innerHeight - 80);
        return {
            top: (window.innerHeight - h) / 2,
            left: (window.innerWidth - w) / 2,
            width: w,
            height: h,
            right: (window.innerWidth + w) / 2,
            bottom: (window.innerHeight + h) / 2,
            x: (window.innerWidth - w) / 2,
            y: (window.innerHeight - h) / 2,
            toJSON: () => {}
        } as DOMRect;
    }
    
    private updateHighlight(rect: DOMRect): void {
        const padding = 12;
        this.highlightStyle = `
            top: ${rect.top - padding}px;
            left: ${rect.left - padding}px;
            width: ${Math.max(rect.width + padding * 2, 20)}px;
            height: ${Math.max(rect.height + padding * 2, 20)}px;
        `;
    }
    
    private updateTooltip(rect: DOMRect): void {
        const step = this.currentStep!;
        const position = step.position || 'bottom';
        const tooltipWidth = 400;
        const tooltipHeight = 250;
        const spacing = 20;
        
        let top = 0;
        let left = 0;
        
        switch (position) {
            case 'top':
                top = rect.top - tooltipHeight - spacing;
                left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                break;
            case 'bottom':
                top = rect.bottom + spacing;
                left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                break;
            case 'left':
                top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
                left = rect.left - tooltipWidth - spacing;
                break;
            case 'right':
                top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
                left = rect.right + spacing;
                break;
            case 'center':
                top = window.innerHeight / 2 - tooltipHeight / 2;
                left = window.innerWidth / 2 - tooltipWidth / 2;
                break;
        }
        
        // Keep tooltip within viewport
        top = Math.max(20, Math.min(top, window.innerHeight - tooltipHeight - 20));
        left = Math.max(20, Math.min(left, window.innerWidth - tooltipWidth - 20));
        
        this.tooltipStyle = `
            top: ${top}px;
            left: ${left}px;
        `;
    }
    
    get progressValue(): number {
        if (this.totalSteps === 0) return 0;
        return ((this.currentStepIndex + 1) / this.totalSteps) * 100;
    }
    
    get isLastStep(): boolean {
        return this.currentStepIndex === this.totalSteps - 1;
    }
    
    nextStep(): void {
        this.tourService.nextStep();
    }
    
    previousStep(): void {
        this.tourService.previousStep();
    }
    
    skipTour(): void {
        if (this.currentTour) {
            this.tourService.completeTour(this.currentTour.id);
        } else {
            this.tourService.stopTour();
        }
    }
    
    onOverlayClick(event: MouseEvent): void {
        // Only close if clicking directly on overlay, not on tooltip
        const target = event.target as HTMLElement;
        if (target.classList.contains('tour-overlay')) {
            // Don't close on overlay click - require explicit skip/close
        }
    }
}
